import dynamicField from "$routes/_lib/dynamic-form-fields/dynamic-field-hoc";
import type { FieldMap } from "$routes/_lib/dynamic-form-fields/dynamic-form-fields-context";
import React from "react";
import type { Property } from "../property-type";
import DynamicResult, {
  DefaultDynamicResultComponent,
  DynamicResultFieldsContext,
  type DynamicResultProps,
} from "./dynamic-result";
import InvestigationResults from "./investigation-results";
import UnitResult from "./unit-result";

const withValue = (property: Property) => {
  if (property.value && property.value !== property.default) {
    return true;
  }

  if (property.orderedProperties) {
    return property.orderedProperties.some((childProperty) =>
      withValue(childProperty),
    );
  }

  return false;
};

const DynamicInvestigationResults = ({
  property: { orderedProperties },
  print,
}: DynamicResultProps) => {
  const items = orderedProperties.reduce(
    (result, currentProperty: Property, i: number) => {
      if (withValue(currentProperty)) {
        // Safe to use array index as key as the order does not change

        result.push([
          currentProperty,
          <DynamicResult property={currentProperty} key={i} />,
        ]);
      }

      return result;
    },
    [],
  ) as [Property, React.ReactElement][];

  return <InvestigationResults items={items} print={print} />;
};

const DynamicInvestigationResultsContainer = (
  props: DynamicResultProps & { fieldMap?: FieldMap<DynamicResultProps> },
): React.ReactElement => {
  const { fieldMap, ...restProps } = props;
  return (
    <DynamicResultFieldsContext.Provider value={{ fieldMap }}>
      <DynamicInvestigationResults {...restProps} />
    </DynamicResultFieldsContext.Provider>
  );
};

/**
 * Maps to custom components and types of the property
 */
const defaultFieldMap = {
  BmiInput: dynamicField(UnitResult),
  UnitInput: dynamicField(UnitResult),
  TextArea: dynamicField(DefaultDynamicResultComponent),
  object: dynamicField(DynamicInvestigationResults),
  TEWSScoreInput: dynamicField(UnitResult),
};

DynamicInvestigationResults.defaultProps = {
  print: false,
  property: {
    orderedProperties: [],
  },
};

DynamicInvestigationResultsContainer.defaultProps = {
  fieldMap: defaultFieldMap,

  print: false,
};

export default React.memo(DynamicInvestigationResultsContainer);
